// InstructionView.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  useCallback,
} from 'react';
import { encode } from '@/base64';
import { focusWidget, useMobile } from '@/util';
import { T } from '@/components/T';
import { FroalaTextareaView } from '@/components/Froala/FroalaTextareaView';
import { ReadOnlySegmentTimeline } from './ReadOnlySegmentTimeline';
import { normalizeSrclang, labelFromLang } from './lang';
import { CourseLanguageContext } from '@/components/Contexts/CourseLanguageContext';
import { ArrowLeft, Cross } from '@teo/components/icons';
import { Button } from '@teo/components';
import { useTranslation } from 'react-i18next';
import { Video } from '@/components/Froala/widgets/ChatGPT/Icon/index';

/* ---------- lang / tracks helpers ---------- */
const canonicalizeLang = (tag = '') => {
  const s = String(tag).trim();
  if (!s) return '';
  const parts = s.split('-');
  if (parts.length === 1) return parts[0].toLowerCase();
  return parts[0].toLowerCase() + '-' + parts[1].toUpperCase();
};

const base = (l = '') => String(l).toLowerCase().split('-')[0] || '';

const fileExt = (url = '') =>
  url.split('?')[0].toLowerCase().split('.').pop() || '';

const guessMimeFromUrl = (url = '') => {
  const ext = fileExt(url);
  if (ext === 'mp4') return 'video/mp4';
  if (ext === 'webm') return 'video/webm';
  if (ext === 'm3u8') return 'application/vnd.apple.mpegurl';
  if (ext === 'mov') return '';
  return '';
};

const buildTracks = (state) => {
  if (Array.isArray(state?.tracks) && state.tracks.length) {
    return state.tracks
      .filter((t) => t?.src)
      .map((t) => {
        const srclang = canonicalizeLang(normalizeSrclang(t.srclang || t.lang));
        return {
          kind: t.kind || 'subtitles',
          src: t.src,
          srclang,
          label: t.label || labelFromLang(srclang),
          default: Boolean(t.default),
        };
      });
  }

  if (state?.captionsVtt?.url) {
    const srclang = canonicalizeLang(
      normalizeSrclang(state?.captionsVtt?.srclang || 'und')
    );

    return [
      {
        kind: 'subtitles',
        src: state.captionsVtt.url,
        label: labelFromLang(srclang),
        srclang,
        default: true,
      },
    ];
  }

  return [];
};

const pickBestTrackIndex = (courseLang, tracks) => {
  if (!tracks?.length) return -1;

  const want = canonicalizeLang(courseLang);
  const wantBase = base(want);

  const exact = tracks.findIndex((t) => canonicalizeLang(t.srclang) === want);
  if (exact >= 0) return exact;

  const byBase = tracks.findIndex(
    (t) => base(canonicalizeLang(t.srclang)) === wantBase
  );
  if (byBase >= 0) return byBase;

  const anyDefault = tracks.findIndex((t) => t.default);
  if (anyDefault >= 0) return anyDefault;

  return 0;
};

/* ---------- Safe Froala Wrapper ---------- */
class FroalaErrorBoundary extends React.Component {
  constructor(p) {
    super(p);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      const html = String(this.props.html || '');
      return (
        <div
          className="prose max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }

    return this.props.children;
  }
}

const StepBody = ({ step }) => {
  const html = String(step?.html_enhanced ?? step?.html ?? '');
  return (
    <FroalaErrorBoundary html={html}>
      <FroalaTextareaView value={html} />
    </FroalaErrorBoundary>
  );
};
/* ---------------------------------------- */

const forceTrackMode = (videoEl, selectedTrackIndex) => {
  if (!videoEl?.textTracks) return;

  for (let i = 0; i < videoEl.textTracks.length; i++) {
    videoEl.textTracks[i].mode =
      i === selectedTrackIndex && selectedTrackIndex >= 0
        ? 'showing'
        : 'hidden';
  }
};

/* ========== HOISTED CHILDREN (renderers) ========== */
const Accordion = React.memo(function Accordion({
  steps,
  activeIdx,
  setActiveIdx,
  seekToIdx,
  videoRef,
  videoSrc,
  onTimeUpdate,
  durationSec,
  setDurationSec,
  durationVideo,
  currentTimeSec,
  seekToSec,
  tracks,
  selectedTrackIndex,
  setIsPausedBySegment,
  isPausedBySegment,
  restoreVideoPosition,
  playbackSessionKey,
}) {
  const { t } = useTranslation();

  const sourceType = useMemo(
    () => guessMimeFromUrl(videoSrc || ''),
    [videoSrc]
  );

  const tracksKey = useMemo(
    () => tracks.map((t) => `${t.src}|${t.srclang}`).join(','),
    [tracks]
  );

  return (
    <div className="mx-auto w-full min-w-0 max-w-[1000px] overflow-x-hidden">
      {videoSrc ? (
        <>
          <div className="mb-2 overflow-hidden rounded-xl border border-grey-02 bg-black lg:mb-4">
            <div className="aspect-video w-full">
              <video
                data-scorm-instruction-video
                key={`${videoSrc}|${tracksKey}|${playbackSessionKey}|accordion`}
                ref={videoRef}
                preload="metadata"
                controls
                playsInline
                crossOrigin="anonymous"
                className="h-full w-full"
                style={{ objectFit: 'contain' }}
                onTimeUpdate={onTimeUpdate}
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration || 0;
                  setDurationSec(
                    d > 2 && d !== Infinity
                      ? d
                      : durationVideo > 0
                      ? durationVideo
                      : d
                  );
                  forceTrackMode(e.currentTarget, selectedTrackIndex);
                  restoreVideoPosition?.(e.currentTarget);
                }}
                onLoadedData={(e) => {
                  forceTrackMode(e.currentTarget, selectedTrackIndex);
                }}
              >
                {sourceType ? (
                  <source src={videoSrc} type={sourceType} />
                ) : (
                  <source src={videoSrc} />
                )}
                {tracks.map((t, idx) => (
                  <track
                    key={`${t.src}-${idx}`}
                    kind={t.kind || 'subtitles'}
                    src={t.src}
                    srcLang={canonicalizeLang(t.srclang)}
                    label={
                      t.label || labelFromLang(canonicalizeLang(t.srclang))
                    }
                    default={idx === selectedTrackIndex}
                  />
                ))}
                Sorry, your browser does not support embedded videos.
              </video>
            </div>
          </div>

          {steps.length ? (
            <div className="mb-4 lg:mb-6">
              <ReadOnlySegmentTimeline
                steps={steps}
                durationSec={durationSec}
                currentTimeSec={currentTimeSec}
                onSeek={seekToSec}
                onSelectIndex={(i) => {
                  setActiveIdx(i);
                  seekToIdx(i);
                }}
                setIsPausedBySegment={setIsPausedBySegment}
                v={videoRef.current}
              />
            </div>
          ) : (
            ''
          )}
        </>
      ) : null}

      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-medium text-black">
          {t('courseTypes.instructions')}
        </h3>
        <p className="text-base text-grey-07">
          {steps.length} {t('pages.create_edit_course.video_instruction.steps')}
        </p>
      </div>

      {steps.length ? (
        <div className="flex min-w-0 flex-col gap-3 overflow-x-hidden bg-white">
          {steps.map((s, i) => {
            const open = i === activeIdx;

            return (
              <div
                key={s.id || i}
                data-scorm-instruction-card={i}
                className={`min-w-0 rounded-lg border ${
                  open ? 'border-secondary-04' : 'border-grey-02'
                }`}
              >
                <button
                  type="button"
                  data-scorm-instruction-step={i}
                  className="flex w-full min-w-0 items-start gap-3 px-4 py-3 text-left"
                  onClick={() => {
                    setIsPausedBySegment(false);
                    const v = videoRef.current;
                    v?.play?.().catch(() => undefined);
                    setActiveIdx(i);
                    seekToIdx(i);
                  }}
                >
                  <div
                    className={`rounded-[4px] px-2 text-white ${
                      open ? 'bg-secondary-04' : 'bg-grey-04'
                    }`}
                  >
                    {i + 1}
                  </div>

                  <span
                    className={`
                      min-w-0 flex-1 basis-0 break-words text-base font-medium leading-snug
                      ${open ? '' : 'line-clamp-2'}
                    `}
                    style={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: open ? 'unset' : 2,
                      overflow: open ? 'visible' : 'hidden',
                    }}
                  >
                    {s.title || <T>Untitled step</T>}
                  </span>
                </button>

                {open && (
                  <div className="min-w-0 overflow-x-hidden px-4 pb-4 text-sm text-grey-06">
                    <StepBody step={s} />
                  </div>
                )}

                {isPausedBySegment && open && s?.proceed !== true ? (
                  <div className="mx-3 mb-3">
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsPausedBySegment(false);
                        const v = videoRef.current;
                        v?.play?.().catch(() => undefined);
                      }}
                    >
                      {t(
                        'pages.create_edit_course.video_instruction.next_step'
                      )}
                    </Button>
                  </div>
                ) : (
                  ''
                )}
              </div>
            );
          })}
        </div>
      ) : (
        ''
      )}
    </div>
  );
});

const Immersive = React.memo(function Immersive(props) {
  const {
    title,
    steps,
    activeIdx,
    setIsFullscreen,
    videoRef,
    videoSrc,
    onTimeUpdate,
    onSelectStep,
    layoutMode,
    isNarrow,
    durationSec,
    setDurationSec,
    durationVideo,
    currentTimeSec,
    seekToSec,
    tracks,
    selectedTrackIndex,
    viewMobile,
    lesson,
    setIsPausedBySegment,
    isPausedBySegment,
    isExtraInfo,
    restoreVideoPosition,
    capturePlaybackPosition,
    playbackSessionKey,
  } = props;

  const sourceType = useMemo(
    () => guessMimeFromUrl(videoSrc || ''),
    [videoSrc]
  );

  const isMobile = useMobile();
  const { t } = useTranslation();

  const tracksKey = useMemo(
    () => tracks.map((t) => `${t.src}|${t.srclang}`).join(','),
    [tracks]
  );

  const headerRef = useRef(null);
  const videoBlockRef = useRef(null);

  const effectiveMode = useMemo(() => {
    if (layoutMode !== 'auto') return layoutMode;
    return isNarrow ? 'stack' : 'sidebar';
  }, [layoutMode, isNarrow]);

  const HEADER_H = isExtraInfo ? 168 : 104;
  const TL_H = 48;

  const isSidebar = effectiveMode === 'sidebar';
  const isStack = !isSidebar;

  const [heightBlocks, setHeightBlocks] = useState({ header: 0, video: 0 });

  useEffect(() => {
    const update = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const videoHeight = videoBlockRef.current?.offsetHeight || 0;
      setHeightBlocks({ header: headerHeight, video: videoHeight });
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[999] bg-grey-09 ${
        isStack || viewMobile ? 'overflow-y-auto' : 'overflow-y-hidden'
      } ${viewMobile ? 'bg-white' : ''} ${isExtraInfo ? 'top-[64px]' : ''}`}
      style={{ overscrollBehaviorX: 'none', maxWidth: '100vw' }}
    >
      <div className={`${viewMobile ? 'mx-auto w-[450px] bg-grey-09' : ''}`}>
        <div className="flex w-full flex-col">
          <div
            ref={headerRef}
            className="flex items-center justify-between p-3 md:p-4 lg:p-8"
          >
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size={isMobile ? 'sm' : 'md'}
                className="!px-2"
                onClick={() => {
                  capturePlaybackPosition?.();
                  setIsFullscreen(false);
                }}
              >
                <ArrowLeft className="h-6 w-6 text-white" />
              </Button>

              <Video className="h-7 w-7 min-w-7 text-grey-04" />

              <div className="text-sm font-semibold text-white md:text-base">
                {lesson?.name || title || 'Instruction'}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size={isMobile ? 'sm' : 'md'}
                className="!px-2 text-sm text-white lg:px-4"
                onClick={() => {
                  capturePlaybackPosition?.();
                  setIsFullscreen(false);
                }}
              >
                {isSidebar && !viewMobile ? (
                  t(
                    'pages.create_edit_course.video_instruction.exit_full_screen'
                  )
                ) : (
                  <Cross className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>

          <div
            className={
              isSidebar && !viewMobile
                ? 'grid min-h-0 w-full grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_460px] lg:pr-0 xl:grid-cols-[minmax(0,1fr)_540px]'
                : 'flex w-full flex-col'
            }
            style={
              isSidebar && !viewMobile
                ? { height: `calc(100vh - ${HEADER_H}px)`, overflow: 'hidden' }
                : undefined
            }
          >
            <div
              ref={videoBlockRef}
              className={
                isSidebar && !viewMobile
                  ? 'relative flex min-h-0 bg-grey-10 px-4'
                  : 'video_block flex flex-col bg-grey-10 p-3 md:p-4'
              }
            >
              <div className="relative my-auto flex max-h-max min-h-0 w-full flex-col">
                <div className="mx-auto">
                  <video
                    key={`${videoSrc}|${tracksKey}|${playbackSessionKey}|immersive`}
                    ref={videoRef}
                    preload="metadata"
                    controls
                    playsInline
                    crossOrigin="anonymous"
                    className="h-auto w-auto rounded-lg"
                    style={{
                      objectFit: 'contain',
                      ...(isStack
                        ? { maxHeight: '35vh' }
                        : {
                            maxHeight: 'max-content',
                            height: '70vh',
                          }),
                    }}
                    onLoadedMetadata={(e) => {
                      const d = e.currentTarget.duration || 0;
                      setDurationSec(
                        d > 2 && d !== Infinity
                          ? d
                          : durationVideo > 0
                          ? durationVideo
                          : d
                      );
                      forceTrackMode(e.currentTarget, selectedTrackIndex);
                      restoreVideoPosition?.(e.currentTarget);
                    }}
                    onLoadedData={(e) => {
                      forceTrackMode(e.currentTarget, selectedTrackIndex);
                    }}
                    onTimeUpdate={onTimeUpdate}
                  >
                    {sourceType ? (
                      <source src={videoSrc || ''} type={sourceType} />
                    ) : (
                      <source src={videoSrc || ''} />
                    )}
                    {tracks.map((t, idx) => (
                      <track
                        key={`${t.src}-${idx}`}
                        kind={t.kind || 'subtitles'}
                        src={t.src}
                        srcLang={canonicalizeLang(t.srclang)}
                        label={
                          t.label || labelFromLang(canonicalizeLang(t.srclang))
                        }
                        default={idx === selectedTrackIndex}
                      />
                    ))}
                    Sorry, your browser does not support embedded videos.
                  </video>
                </div>

                <div
                  className="pt-2 lg:pt-3"
                  style={{ height: isMobile ? '40px' : TL_H }}
                >
                  <ReadOnlySegmentTimeline
                    steps={steps}
                    durationSec={durationSec}
                    currentTimeSec={currentTimeSec}
                    onSeek={(sec) => seekToSec(sec)}
                    onSelectIndex={(i) => onSelectStep(i)}
                    setIsPausedBySegment={setIsPausedBySegment}
                    v={videoRef.current}
                  />
                </div>
              </div>
            </div>

            <aside
              className={
                isSidebar && !viewMobile
                  ? 'h-full min-h-0 overflow-y-auto p-3'
                  : 'p-3'
              }
              style={
                isSidebar && !viewMobile
                  ? { maxHeight: 'calc(100vh - 0px)' }
                  : undefined
              }
            >
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-white md:text-base">
                  {t('courseTypes.instructions')}
                </h3>
                <p className="text-sm text-grey-03 md:text-base">
                  {steps.length}{' '}
                  {t('pages.create_edit_course.video_instruction.steps')}
                </p>
              </div>

              <div
                className="flex flex-col gap-2"
                style={
                  isSidebar && !viewMobile
                    ? {
                        paddingLeft: 0,
                      }
                    : {
                        paddingLeft: 0,
                        maxHeight: `calc(100vh - ${heightBlocks?.video}px - 48px)`,
                        overflowY: 'auto',
                      }
                }
              >
                {steps.map((s, i) => {
                  const isActive = i === activeIdx;

                  return (
                    <div
                      key={s.id || i}
                      onClick={() => {
                        setIsPausedBySegment(false);
                        const v = videoRef.current;
                        v?.play?.().catch(() => undefined);
                        onSelectStep(i);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onSelectStep(i);
                        }
                      }}
                      className={`
                        flex min-w-0 cursor-pointer flex-col rounded-md p-3 md:p-4
                        ${
                          isActive
                            ? 'border border-secondary-04'
                            : 'border border-grey-07 hover:bg-grey-08'
                        }
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300
                      `}
                    >
                      <div className="flex min-w-0 items-center gap-3 text-left">
                        <div
                          className={`rounded-[4px] px-2 text-white ${
                            isActive ? 'bg-secondary-04' : 'bg-grey-07'
                          }`}
                        >
                          {i + 1}
                        </div>

                        <div className="flex w-full min-w-0 items-center justify-between gap-3 pl-2">
                          <span className="min-w-0 flex-1 basis-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-white md:text-base">
                            {s.title || <T>Untitled step</T>}
                          </span>
                        </div>
                      </div>

                      {isActive && (
                        <div className="mt-2 min-w-0 overflow-x-hidden text-sm text-grey-03">
                          <StepBody step={s} />
                        </div>
                      )}

                      {isPausedBySegment && isActive && s?.proceed !== true ? (
                        <div className="mt-3">
                          <Button
                            className="!border-grey-03 !text-grey-03"
                            variant="outline"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsPausedBySegment(false);
                              const v = videoRef.current;
                              v?.play?.().catch(() => undefined);
                            }}
                          >
                            {t(
                              'pages.create_edit_course.video_instruction.next_step'
                            )}
                          </Button>
                        </div>
                      ) : (
                        ''
                      )}
                    </div>
                  );
                })}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
});

/* ========================================================== */

export const InstructionView = ({
  state,
  index,
  resultPages,
  lesson,
  isExtraInfo = false,
}) => {
  const { courseLang } = useContext(CourseLanguageContext);
  const { t } = useTranslation();

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const timeGateRef = useRef(0);
  const lastIdxRef = useRef(0);
  const restoreAppliedRef = useRef('');
  const mountedModeRef = useRef('accordion');

  const [viewMobile, setViewMobile] = useState(false);
  const [durationVideo] = useState(state?.durationVideo || 0);
  const [isPausedBySegment, setIsPausedBySegment] = useState(false);

  const [activeIdx, setActiveIdx] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [durationSec, setDurationSec] = useState(0);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [wasPlayingBeforeSwitch, setWasPlayingBeforeSwitch] = useState(false);
  const [playbackSessionKey, setPlaybackSessionKey] = useState(0);

  const [layoutMode, setLayoutMode] = useState('auto');
  const [isNarrow, setIsNarrow] = useState(false);

  const steps = useMemo(() => {
    const arr = Array.isArray(state?.steps) ? [...state.steps] : [];
    arr.sort((a, b) => (a.start_ms ?? 0) - (b.start_ms ?? 0));
    return arr;
  }, [state?.steps]);

  const starts = useMemo(() => steps.map((s) => s.start_ms ?? 0), [steps]);
  const ends = useMemo(
    () => steps.map((s) => s.end_ms ?? Number.MAX_SAFE_INTEGER),
    [steps]
  );

  const tracks = useMemo(() => buildTracks(state), [state]);

  const selectedTrackIndex = useMemo(
    () => pickBestTrackIndex(courseLang, tracks),
    [courseLang, tracks]
  );

  const tracksKey = useMemo(
    () => tracks.map((t) => `${t.src}|${t.srclang}`).join(','),
    [tracks]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const viewVersion = queryParams.get('view');
    if (viewVersion === 'mobile') {
      setViewMobile(true);
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const apply = () => setIsNarrow(!!mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    if (isFullscreen) root.classList.add('fullscreen-wrapper');
    else root.classList.remove('fullscreen-wrapper');

    return () => root.classList.remove('fullscreen-wrapper');
  }, [isFullscreen]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const apply = () => {
      forceTrackMode(v, selectedTrackIndex);
    };

    apply();

    const onAdd = () => setTimeout(apply, 0);

    if (v.textTracks && typeof v.textTracks.addEventListener === 'function') {
      v.textTracks.addEventListener('addtrack', onAdd);
    }

    return () => {
      if (
        v.textTracks &&
        typeof v.textTracks.removeEventListener === 'function'
      ) {
        v.textTracks.removeEventListener('addtrack', onAdd);
      }
    };
  }, [selectedTrackIndex, tracksKey, state?.url, playbackSessionKey]);

  const HYST_MS = 250;

  const detectIdx = useCallback(
    (ms) => {
      const n = steps.length;
      if (!n) return 0;

      for (let i = 0; i < n; i++) {
        const s = (starts[i] ?? 0) - HYST_MS;
        const e = (ends[i] ?? s) - HYST_MS;

        if (ms >= s && ms < e) return i;
      }

      for (let i = 0; i < n; i++) {
        if (ms < (starts[i] ?? Number.POSITIVE_INFINITY)) return i;
      }

      return n - 1;
    },
    [steps.length, starts, ends]
  );

  const capturePlaybackPosition = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;

    const sec = v.currentTime || 0;
    const wasPlaying = !v.paused && !v.ended;

    setCurrentTimeSec(sec);
    setWasPlayingBeforeSwitch(wasPlaying);

    const idx = detectIdx(sec * 1000);
    if (idx !== lastIdxRef.current) {
      lastIdxRef.current = idx;
      setActiveIdx(idx);
    }

    v.pause();
    setPlaybackSessionKey((prev) => prev + 1);
  }, [detectIdx]);

  const restoreVideoPosition = useCallback(
    (videoEl) => {
      if (!videoEl) return;

      const modeKey = isFullscreen ? 'immersive' : 'accordion';
      const restoreKey = `${modeKey}|${playbackSessionKey}|${currentTimeSec}|${activeIdx}|${wasPlayingBeforeSwitch}`;

      if (restoreAppliedRef.current === restoreKey) return;
      restoreAppliedRef.current = restoreKey;

      const fallbackStepSec = (starts[activeIdx] || 0) / 1000;
      const targetSec =
        Number.isFinite(currentTimeSec) && currentTimeSec > 0
          ? currentTimeSec
          : fallbackStepSec;

      if (!Number.isFinite(targetSec)) return;

      const applyTimeAndPlayback = () => {
        if (Math.abs((videoEl.currentTime || 0) - targetSec) > 0.2) {
          videoEl.currentTime = targetSec;
        }

        if (wasPlayingBeforeSwitch) {
          const playPromise = videoEl.play?.();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => undefined);
          }
        } else {
          videoEl.pause?.();
        }
      };

      if (videoEl.readyState >= 1) {
        applyTimeAndPlayback();
        return;
      }

      const onLoaded = () => {
        applyTimeAndPlayback();
        videoEl.removeEventListener('loadedmetadata', onLoaded);
      };

      videoEl.addEventListener('loadedmetadata', onLoaded);
    },
    [
      activeIdx,
      currentTimeSec,
      starts,
      wasPlayingBeforeSwitch,
      playbackSessionKey,
      isFullscreen,
    ]
  );

  const seekToSec = useCallback(
    (sec) => {
      const v = videoRef.current;
      const clamped = Math.max(0, Math.min(sec, durationSec || sec));

      if (v) {
        const wasPlaying = !v.paused && !v.ended;
        v.currentTime = clamped;

        if (wasPlaying) {
          v.play?.().catch(() => undefined);
        }
      }

      const ms = clamped * 1000;
      const idx = detectIdx(ms);

      if (idx !== lastIdxRef.current) {
        lastIdxRef.current = idx;
        setActiveIdx(idx);
      }

      setCurrentTimeSec(clamped);
    },
    [detectIdx, durationSec]
  );

  const seekToIdx = useCallback(
    (idx) => {
      if (idx < 0 || idx >= steps.length) return;

      const t = (starts[idx] || 0) / 1000;

      seekToSec(t);
      setActiveIdx(idx);
      lastIdxRef.current = idx;

      if (typeof history?.replaceState === 'function') {
        history.replaceState(null, '', `#${steps[idx].id}`);
      }
    },
    [steps, starts, seekToSec]
  );

  const nextStep = useCallback(() => {
    seekToIdx(Math.min(activeIdx + 1, steps.length - 1));
  }, [activeIdx, steps.length, seekToIdx]);

  const prevStep = useCallback(() => {
    seekToIdx(Math.max(activeIdx - 1, 0));
  }, [activeIdx, seekToIdx]);

  const onTimeUpdate = useCallback(() => {
    const now = performance.now();
    if (now - timeGateRef.current < 120) return;
    timeGateRef.current = now;

    const v = videoRef.current;
    if (!v) return;

    const sec = v.currentTime || 0;
    setCurrentTimeSec(sec);

    const idx = detectIdx(sec * 1000);
    const currentSeg = steps[idx];

    if (currentSeg && currentSeg.proceed !== true) {
      const segEndSec = (currentSeg.end_ms || 0) / 1000;

      if (Math.abs(sec - segEndSec) < 0.5) {
        v.pause();
        setWasPlayingBeforeSwitch(false);
        setIsPausedBySegment(true);
        return;
      }
    }

    if (idx !== lastIdxRef.current) {
      lastIdxRef.current = idx;
      setActiveIdx(idx);
    }
  }, [detectIdx, steps]);

  useEffect(() => {
    if (!steps.length) return;

    const params = new URLSearchParams(window.location.search);
    const qId = params.get('step') || (window.location.hash || '').slice(1);

    const foundIndex = qId ? steps.findIndex((s) => s.id === qId) : -1;
    const startIndex = foundIndex >= 0 ? foundIndex : 0;

    setActiveIdx(startIndex);
    lastIdxRef.current = startIndex;

    const v = videoRef.current;
    if (!v) return;

    const setMeta = () => {
      const startSec = (starts[startIndex] || 0) / 1000;
      v.currentTime = startSec;
      setCurrentTimeSec(startSec);
      setWasPlayingBeforeSwitch(false);
    };

    if (v.readyState >= 1) setMeta();
    else v.addEventListener('loadedmetadata', setMeta, { once: true });
  }, [steps.length, steps, starts]);

  useEffect(() => {
    mountedModeRef.current = isFullscreen ? 'immersive' : 'accordion';
  }, [isFullscreen]);

  return (
    <div
      className={`flex flex-col gap-4 pb-4 pt-0 lg:pt-4 ${
        !resultPages ? 'rounded-lg' : 'mt-2'
      }`}
      data-state={encode(JSON.stringify(state))}
      data-scorm-next-label={t(
        'pages.create_edit_course.video_instruction.next_step'
      )}
      onFocus={() => focusWidget(state?.uid)}
      ref={containerRef}
    >
      <div
        className={`${
          viewMobile ? '!flex-col !items-start' : ''
        } flex flex-col gap-3 lg:flex-row lg:items-center`}
      >
        <div className="flex items-center gap-4">
          <Video className="h-6 w-7 min-w-7 text-grey-06" />
          <h3 className="text-xl">
            {lesson?.name || state.title || 'Instruction'}
          </h3>
        </div>

        <div>
          <Button
            variant="outline"
            data-scorm-instruction-fullscreen
            data-scorm-exit-label={t(
              'pages.create_edit_course.video_instruction.exit_full_screen'
            )}
            onClick={() => {
              capturePlaybackPosition();
              setIsFullscreen(true);
            }}
          >
            {t('pages.create_edit_course.video_instruction.view_full_screen')}
          </Button>
        </div>
      </div>

      <>
        {isFullscreen && (
          <Immersive
            title={state.title}
            steps={steps}
            activeIdx={activeIdx}
            setIsFullscreen={setIsFullscreen}
            videoRef={videoRef}
            videoSrc={state?.url}
            onTimeUpdate={onTimeUpdate}
            nextStep={nextStep}
            prevStep={prevStep}
            onSelectStep={(i) => seekToIdx(i)}
            layoutMode={layoutMode}
            setLayoutMode={setLayoutMode}
            isNarrow={isNarrow}
            durationSec={durationSec}
            setDurationSec={setDurationSec}
            durationVideo={durationVideo}
            currentTimeSec={currentTimeSec}
            seekToSec={seekToSec}
            tracks={tracks}
            selectedTrackIndex={selectedTrackIndex}
            viewMobile={viewMobile}
            lesson={lesson}
            setIsPausedBySegment={setIsPausedBySegment}
            isPausedBySegment={isPausedBySegment}
            isExtraInfo={isExtraInfo}
            restoreVideoPosition={restoreVideoPosition}
            capturePlaybackPosition={capturePlaybackPosition}
            playbackSessionKey={playbackSessionKey}
          />
        )}

        {!isFullscreen && (
          <Accordion
            steps={steps}
            activeIdx={activeIdx}
            setActiveIdx={setActiveIdx}
            seekToIdx={seekToIdx}
            videoRef={videoRef}
            videoSrc={state?.url}
            onTimeUpdate={onTimeUpdate}
            durationSec={durationSec}
            setDurationSec={setDurationSec}
            durationVideo={durationVideo}
            currentTimeSec={currentTimeSec}
            seekToSec={seekToSec}
            tracks={tracks}
            setIsPausedBySegment={setIsPausedBySegment}
            selectedTrackIndex={selectedTrackIndex}
            isPausedBySegment={isPausedBySegment}
            restoreVideoPosition={restoreVideoPosition}
            playbackSessionKey={playbackSessionKey}
          />
        )}
      </>
    </div>
  );
};

InstructionView.syncStates = (state, answer, correction) => {
  if (answer && !answer.uid) answer.uid = state?.uid;
  if (correction && !correction.uid) correction.uid = state?.uid;
  return { answer, correction };
};
